import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Grid,
  Box,
  Typography,
  Card,
  Autocomplete,
  TextField,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import { keyframes } from "@mui/system";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import MedicalAbstractBackground from "./MedicalAbstractBackground ";

const CustomStepper = ({ title }) => (
  <Box mb={3} display="flex" justifyContent="center">
    <Typography
      variant="h6"
      sx={{ fontWeight: "bold", color: "#2e7fae", textAlign: "center" }}
    >
      {title}
    </Typography>
  </Box>
);

function DoctorMapping() {
  const loginData = JSON.parse(localStorage.getItem("loginData"));
  const userid = loginData?.userid;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [renderingDoctorNames, setRenderingDoctorNames] = useState([]);
  const [dutyDoctorNames, setDutyDoctorNames] = useState([]);

  const [renderingDoctorInput, setRenderingDoctorInput] = useState("");
  const [dutyDoctorInput, setDutyDoctorInput] = useState("");
  const [selectedDoctors, setSelectedDoctors] = useState([]);

  const [doctorMap, setDoctorMap] = useState({
    ccid: userid,
    date: new Date().toISOString().slice(0, 10),
    renderingdoctorId: "",
    dutydoctorIds: [],
  });

  const renderingSearchRef = useRef(null);
  const dutySearchRef = useRef(null);

  // 🔹 Fetch doctor names for a given type
  const fetchDoctorNames = async (doctorType, setter, searchTerm = "") => {
    try {
      const secretKey = await fetchSecretKey();
      const token = loginData?.token;
      let response;

      if (searchTerm.trim()) {
        response = await axios.post(
          `/api/doctorregistration/search-all`,
          { searchTerm, doctorType },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.get(
          `/api/doctorregistration/doctorType/${doctorType}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);
        setter(Array.isArray(decrypted) ? decrypted : []);
      }
    } catch (err) {
      console.error("Error fetching doctors:", err);
    }
  };

  // 🔹 Debounced search for Rendering Doctor
  useEffect(() => {
    if (renderingSearchRef.current) clearTimeout(renderingSearchRef.current);
    renderingSearchRef.current = setTimeout(() => {
      if (renderingDoctorInput !== "") {
        fetchDoctorNames("RENDERING_DOCTOR", setRenderingDoctorNames, renderingDoctorInput);
      } else {
        fetchDoctorNames("RENDERING_DOCTOR", setRenderingDoctorNames);
      }
    }, 500);
    return () => clearTimeout(renderingSearchRef.current);
  }, [renderingDoctorInput]);

  // 🔹 Debounced search for Duty Doctor
  useEffect(() => {
    if (dutySearchRef.current) clearTimeout(dutySearchRef.current);
    dutySearchRef.current = setTimeout(() => {
      if (dutyDoctorInput !== "") {
        fetchDoctorNames("DUTY_DOCTOR", setDutyDoctorNames, dutyDoctorInput);
      } else {
        fetchDoctorNames("DUTY_DOCTOR", setDutyDoctorNames);
      }
    }, 500);
    return () => clearTimeout(dutySearchRef.current);
  }, [dutyDoctorInput]);

  // 🔹 Initial load
  useEffect(() => {
    fetchDoctorNames("RENDERING_DOCTOR", setRenderingDoctorNames);
    fetchDoctorNames("DUTY_DOCTOR", setDutyDoctorNames);
  }, []);

  // 🔹 Submit Mapping
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctorMap.renderingdoctorId || doctorMap.dutydoctorIds.length === 0) {
      toast.error("Please select Rendering and at least one Duty Doctor.");
      return;
    }

    setLoading(true);
    try {
      const secretKey = await fetchSecretKey();
      const token = loginData?.token;

      const rawPayload = {
        ccid: userid,
        renderingdoctorId: doctorMap.renderingdoctorId,
        dutydoctorIds: doctorMap.dutydoctorIds,
      };

      const encryptedData = encryptBody(rawPayload, secretKey);

      const response = await axios.post(`/api/doctormap/create`, encryptedData, {
        headers: {
          "Content-Type": "text/plain",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const decrypted = decryptBody(response.data, secretKey);

        if (decrypted?.success === false) {
          toast.error(decrypted?.message || "Failed to save doctor mapping.");
        } else {
          toast.success(decrypted?.message || "Doctor mapping saved successfully!");
          navigate(-1);
        }
      }
    } catch (err) {
      console.error("Error submitting mapping:", err);
      toast.error("Failed to save mapping");
    } finally {
      setLoading(false);
    }
  };

  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
            <MedicalAbstractBackground/>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1A237E",
                  position: "absolute",
                  ...pos,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <Card sx={{ width: "1200px", maxWidth: "1200px", padding: 3, margin: "auto" }}>
        <MDBox pt={4} pb={3} px={3}>
          <CustomStepper title="Doctor Mapping" />

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* 🔹 Rendering Doctor Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: "#2e7fae", mb: 1 }}>
                  Rendering Doctor
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <TextField label="Doctor Type" fullWidth value="RENDERING_DOCTOR" disabled />
              </Grid>

              <Grid item xs={6}>
                <Autocomplete
                  options={renderingDoctorNames}
                  getOptionLabel={(option) =>
                    option ? `${option.firstname} ${option.lastname}` : ""
                  }
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={
                    renderingDoctorNames.find(
                      (doc) => doc.id === doctorMap.renderingdoctorId
                    ) || null
                  }
                  onChange={(e, newValue) => {
                    setDoctorMap({
                      ...doctorMap,
                      renderingdoctorId: newValue?.id || "",
                    });

                    if (newValue) {
                      setRenderingDoctorInput(`${newValue.firstname} ${newValue.lastname}`);
                    }
                  }}
                  inputValue={renderingDoctorInput}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === "input") setRenderingDoctorInput(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Rendering Doctor"
                      placeholder="Search or select rendering doctor from the list"
                      variant="outlined"
                       sx={{
      "& .MuiInputBase-root": {
        height: "40px",  
      },
    }}
                    />
                  )}
                />
                <FormHelperText sx={{ mt: 0.5, color: "#666" }}>
                  Select the primary doctor who will be responsible for patient care
                </FormHelperText>
              </Grid>

              {/* 🔹 Duty Doctor Section */}
              <Grid item xs={12} mt={2}>
                <Typography variant="h6" sx={{ color: "#2e7fae", mb: 1 }}>
                  Duty Doctor
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <TextField label="Doctor Type" fullWidth value="DUTY_DOCTOR" disabled />
              </Grid>

              <Grid item xs={6}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={dutyDoctorNames}
                  getOptionLabel={(option) => `${option.firstname} ${option.lastname}`}
                  value={selectedDoctors}
                  onChange={(e, newValue) => {
                    setSelectedDoctors(newValue);
                    setDoctorMap({
                      ...doctorMap,
                      dutydoctorIds: newValue.map((doc) => doc.id),
                    });
                    setDutyDoctorInput("");
                  }}
                  inputValue={dutyDoctorInput}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === "input") setDutyDoctorInput(newValue);
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {`${option.firstname} ${option.lastname}`}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Duty Doctors"
placeholder="Search or select one or more duty doctors from the list"
                      variant="outlined"
                       sx={{
      "& .MuiInputBase-root": {
        height: "40px",   
      },
    }}
                    />
                  )}
                />
                <FormHelperText sx={{ mt: 0.5, color: "#666" }}>
                  Select a duty doctor from the list
                </FormHelperText>
              </Grid>
            </Grid>

            {/* 🔹 Buttons - Grouped on the Right */}
            <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
              <MDButton
                variant="outlined"
                onClick={() => navigate(-1)}
                style={{
                  backgroundColor: "#1A237E",
                  color: "#fff",
                  textTransform: "none",
                  minWidth: "120px",
                }}
              >
                Cancel
              </MDButton>

              <MDButton
                type="submit"
                variant="contained"
                style={{
                  backgroundColor: "#1A237E",
                  color: "#fff",
                  textTransform: "none",
                  minWidth: "120px",
                }}
              >
                Submit
              </MDButton>
            </MDBox>
          </form>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
}

export default DoctorMapping;